/* eslint-disable dxp-rules/no-inline-css -- editor chrome, never page content: it renders only inside a visual editor, so it copies the Studio's own add button and menu rather than pull a design system into a control no visitor ever sees. */
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FC,
  type KeyboardEvent,
  type MouseEvent,
  type SyntheticEvent,
} from 'react';

import type { CmsRendering, CmsSlotAddAction, CmsSlotAdder } from '@eneco/cms-adapter';
import logger from '@eneco/common/log';

import { DATA_SANITY, DRAG_DISABLE } from './edit-dom';
import { ComponentErrorBoundary } from './error-boundary';
import { useCms, type EditAttribute } from './provider';
import { useRendering } from './use-rendering';

type InsertAction = Extract<CmsSlotAddAction, { kind: 'insert' }>;

interface SlotAddTilesProps {
  /** Only the tiles for these slots, matched regardless of case: the ones the layout renders. */
  slots?: readonly string[];
}

/**
 * The "+ Add …" buttons of a container, inside a visual editor only — styled after the Studio's
 * own array Add button and its type menu, so adding in the preview looks like adding in the Studio.
 *
 * A container places this once, wherever its layout has room; what the tiles offer comes entirely
 * from the provider's `CmsSlotAdder`. Without one — a published page, the Sitecore lanes, the server
 * render — nothing renders.
 *
 * Each tile carries its own `data-sanity`, because the editor takes every click on a marked element
 * for "open this": the tile's target opens, not the block around it. An insert tile inserts first,
 * from React's root capture listener, which runs before the editor's listener on the element; its
 * target is the new block, so that same click opens it. A slot that takes several types gets a menu
 * instead, whose options work the same way.
 */
export const SlotAddTiles: FC<SlotAddTilesProps> = ({ slots }) => (
  // Editor chrome only: a failure here must not take the container down with it.
  <ComponentErrorBoundary componentName="SlotAddTiles" fallback={null}>
    <Tiles slots={slots} />
  </ComponentErrorBoundary>
);

const Tiles: FC<SlotAddTilesProps> = ({ slots }) => {
  const { editAttribute, slotAdder, page } = useCms();
  // The page itself when outside any block, as `Placeholder` does.
  const container = useRendering() ?? page.root;
  const actions = useMemo(() => slotAdder?.actionsFor(container, { slots }) ?? [], [slotAdder, container, slots]);
  if (!editAttribute || !slotAdder || actions.length === 0) return null;

  return (
    <>
      {/* Hoisted to the head once, however many tiles render. */}
      <style href="cms-slot-add" precedence="default">
        {STYLES}
      </style>
      {actions.map(action => (
        // A lone tile keeps one key, so it stays the same element when it switches between inserting
        // and opening: the editor tracks hovered elements by node, and would hold on to a swapped-out one.
        <Tile
          key={actions.length === 1 ? 'only' : `${action.kind}:${action.slot}`}
          action={action}
          container={container}
          editAttribute={editAttribute}
          slotAdder={slotAdder}
        />
      ))}
    </>
  );
};

/** Mirrors the editor's own `isModifiedClick`: those clicks are left to the page, so add nothing. */
const isModifiedClick = (event: MouseEvent) =>
  event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

const stop = (event: SyntheticEvent) => event.stopPropagation();

// The Studio's own values (Sanity UI, light theme), read off its array Add button and menu.
const FONT = 'Inter, -apple-system, system-ui, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const buttonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  width: '100%',
  marginTop: '1.5rem',
  padding: '12px',
  border: 0,
  borderRadius: '3px',
  // Opaque, so the label reads the same on a dark section as on a light one.
  background: '#f6f6f8',
  boxShadow: 'inset 0 0 0 1px #e6e6ea',
  color: '#4e556d',
  font: `400 13px/19px ${FONT}`,
  cursor: 'pointer',
};

const panelStyle: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: '50%',
  transform: 'translateX(-50%)',
  // Above Visual Editing's overlay layer (`z-index: 9999999`), whose outlines would cover it.
  zIndex: 2147483647,
  minWidth: '240px',
  maxWidth: '436px',
  maxHeight: '20rem',
  overflow: 'auto',
  margin: 0,
  padding: '4px',
  listStyle: 'none',
  background: '#fff',
  borderRadius: '6px',
  boxShadow:
    'rgba(114, 120, 146, 0.3) 0 0 0 0.5px, rgba(114, 120, 146, 0.1) 0 7px 8px -4px, rgba(114, 120, 146, 0.07) 0 12px 17px 2px, rgba(114, 120, 146, 0.06) 0 5px 22px 4px',
};

const itemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
  padding: '12px',
  border: 0,
  borderRadius: '3px',
  background: 'transparent',
  color: '#383d51',
  font: `400 13px/19px ${FONT}`,
  textAlign: 'left',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
};

/** Set on `<html>` while the pointer is over a tile or its menu. */
const HOVER_FLAG = 'cmsSlotAddHover';

/**
 * Hover and focus, which inline styles cannot express; and, while a tile is hovered, no Visual
 * Editing outline around it. That relies on Visual Editing's internals (`ElementOverlay`'s
 * `data-hovered`/`data-focused` under `<sanity-visual-editing>`): if they change, the outline is back.
 */
const STYLES = `
[data-cms-slot-add]:hover, [data-cms-slot-add]:focus-visible { background: #eeeef1 !important; }
[data-cms-slot-add-item]:hover, [data-cms-slot-add-item]:focus-visible { background: #f6f6f8 !important; outline: none; }
html[data-cms-slot-add-hover] sanity-visual-editing [data-hovered]:not([data-focused]) { visibility: hidden !important; }
`;

/** Raises `HOVER_FLAG` while the pointer is over the element, and drops it on unmount if raised here. */
const useHoverFlag = () => {
  const isRaised = useRef(false);
  useEffect(
    () => () => {
      if (isRaised.current) delete document.documentElement.dataset[HOVER_FLAG];
    },
    [],
  );
  return {
    onPointerEnter: () => {
      isRaised.current = true;
      document.documentElement.dataset[HOVER_FLAG] = '';
    },
    onPointerLeave: () => {
      isRaised.current = false;
      delete document.documentElement.dataset[HOVER_FLAG];
    },
  };
};

const Icon: FC<{ path: string }> = ({ path }) => (
  <svg width="21" height="21" viewBox="0 0 25 25" fill="none" aria-hidden="true" style={{ margin: '-1px 0', flex: 'none', color: '#838693' }}>
    <path d={path} stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
  </svg>
);
const ADD = 'M12.5 5V20M5 12.5H20';
const SEARCH = 'M15.0105 15.0105L20.0104 20.0104M16.5 10.5C16.5 13.8137 13.8137 16.5 10.5 16.5C7.18629 16.5 4.5 13.8137 4.5 10.5C4.5 7.18629 7.18629 4.5 10.5 4.5C13.8137 4.5 16.5 7.18629 16.5 10.5Z';

const Tile: FC<{
  action: CmsSlotAddAction;
  container: CmsRendering;
  editAttribute: EditAttribute;
  slotAdder: CmsSlotAdder;
}> = ({ action, container, editAttribute, slotAdder }) => {
  // The key the next pick of a type not picked yet inserts under.
  const [freshKey, setFreshKey] = useState(() => slotAdder.newKey());
  // Each type picked keeps its key until its block renders: a repeat pick before then reuses it, which
  // the adder treats as already done, and the editor just opens the same block again. Another type
  // gets a key of its own, so it is not mistaken for that block.
  const [pickedKeys, setPickedKeys] = useState<ReadonlyMap<string, string>>(new Map());
  const [failed, setFailed] = useState(false);
  const hover = useHoverFlag();

  const blocks = container.placeholders?.[action.slot];
  useEffect(() => {
    const rendered = new Set(blocks?.map(block => block.uid));
    if (![...pickedKeys.values()].some(key => rendered.has(key))) return;
    setPickedKeys(new Map([...pickedKeys].filter(([, key]) => !rendered.has(key))));
  }, [blocks, pickedKeys]);

  const keyFor = (type: string) => pickedKeys.get(type) ?? freshKey;
  const targetFor = (type: string) =>
    action.kind === 'insert' ? editAttribute(action.pathFor(keyFor(type))) : undefined;

  const add = (event: MouseEvent, type: string) => {
    if (action.kind !== 'insert' || isModifiedClick(event)) return;
    const key = keyFor(type);
    if (!pickedKeys.has(type)) {
      setPickedKeys(current => new Map(current).set(type, key));
      setFreshKey(slotAdder.newKey());
    }
    setFailed(false);
    action.insert(key, type).catch((error: unknown) => {
      setFailed(true);
      logger.warn('pH7tQa', 'Failed to add a block', { error });
    });
  };

  const target = action.kind === 'insert' ? targetFor(action.options[0].type) : editAttribute(action.path);
  if (!target) return null;

  const content =
    action.kind === 'insert' && (action.options.length > 1 || action.other) ? (
      <Menu action={action} targetFor={targetFor} editAttribute={editAttribute} onPick={add} />
    ) : (
      <button
        type="button"
        {...{ [DATA_SANITY]: target, [DRAG_DISABLE]: 'true' }}
        aria-label={action.label}
        onClickCapture={event => (action.kind === 'insert' ? add(event, action.options[0].type) : undefined)}
        // The editor's right-click menu would act on the target — a block that does not exist yet, or
        // the whole container — and its Remove and Duplicate know nothing of the blocks inside it.
        onContextMenuCapture={stop}
        data-cms-slot-add=""
        style={buttonStyle}>
        <Icon path={ADD} />
        {action.label}
      </button>
    );

  return (
    <div {...hover}>
      {content}
      {/* Always in the page, so a screen reader hears the message when it appears. */}
      <span role="status" style={{ color: '#c9244b', font: `400 13px/19px ${FONT}` }}>
        {failed ? 'Could not add it — try again' : ''}
      </span>
    </div>
  );
};

/**
 * A disclosure, not a `role="menu"`: the options are plain buttons in the tab order.
 *
 * The toggle has no edit target, so the container's would apply: it stops its click, mousedown and
 * right-click at React's root, before the editor's listeners on the container — which would open the
 * container or start a drag — ever see them. Each option is an edit target for the new block, as a
 * single-type tile is, so picking one inserts that type and opens it. A pick with the keyboard
 * inserts but opens nothing: the editor only handles clicks on the element it hovers.
 */
const Menu: FC<{
  action: InsertAction;
  targetFor: (type: string) => string | undefined;
  editAttribute: EditAttribute;
  onPick: (event: MouseEvent, type: string) => void;
}> = ({ action, targetFor, editAttribute, onPick }) => {
  const { components } = useCms();
  const [isOpen, setOpen] = useState(false);
  const listId = useId();
  const root = useRef<HTMLDivElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    list.current?.querySelector('button')?.focus();
    // Capture on the document: the editor stops clicks on marked elements, so one would never arrive.
    const closeOutside = (event: PointerEvent) => {
      if (!(event.target instanceof Node && root.current?.contains(event.target))) setOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside, true);
    return () => document.removeEventListener('pointerdown', closeOutside, true);
  }, [isOpen]);

  const close = () => {
    setOpen(false);
    toggle.current?.focus();
  };
  // Later, so the editor's own listener on the option still finds it in the page.
  const closeAfterPick = () => setTimeout(() => setOpen(false));

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' || !isOpen) return;
    event.stopPropagation();
    close();
  };

  const otherTarget = action.other ? editAttribute(action.other.path) : undefined;

  return (
    <div ref={root} onKeyDown={handleKeyDown} style={{ position: 'relative' }}>
      <button
        ref={toggle}
        type="button"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listId : undefined}
        onClickCapture={event => {
          stop(event);
          if (!isModifiedClick(event)) setOpen(current => !current);
        }}
        onMouseDownCapture={stop}
        onContextMenuCapture={stop}
        data-cms-slot-add=""
        style={buttonStyle}>
        <Icon path={ADD} />
        {action.label}
      </button>
      {isOpen ? (
        <ul
          ref={list}
          id={listId}
          aria-label={action.label}
          style={panelStyle}>
          {action.options.map(option => (
            <li key={option.type}>
              <button
                type="button"
                {...{ [DATA_SANITY]: targetFor(option.type), [DRAG_DISABLE]: 'true' }}
                onClickCapture={event => {
                  onPick(event, option.type);
                  closeAfterPick();
                }}
                onContextMenuCapture={stop}
                data-cms-slot-add-item=""
                style={itemStyle}>
                {option.label}
                {Object.hasOwn(components, option.type) ? null : <span style={{ color: '#838693' }}> (no preview)</span>}
              </button>
            </li>
          ))}
          {action.other && otherTarget ? (
            <li>
              <button
                type="button"
                {...{ [DATA_SANITY]: otherTarget, [DRAG_DISABLE]: 'true' }}
                onClickCapture={closeAfterPick}
                onContextMenuCapture={stop}
                data-cms-slot-add-item=""
                style={itemStyle}>
                <Icon path={SEARCH} />
                {action.other.label}
              </button>
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
};
